import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, Landmark, Pencil, Plus, Trash2 } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import ActionsHeaderHelp from '../../../components/ActionsHeaderHelp';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
  EmptyTableState,
  LoadingTableState,
} from '../../../components/StandardTable';
import AdminModal from '../../components/AdminModal';
import { HelpTrigger } from '../../../help';
import { OFFICE_LEVELS, OFFICE_SCOPES, officeLevelLabel } from '../../../constants/elections';

interface Office {
  id: string;
  office_key: string;
  display_label: string;
  office_level: string | null;
  scope: string | null;
  district_scope: string | null;
  sort_order: number | null;
  default_vote_for: number | null;
  state_code: string | null;
  cycle_body_id: string | null;
  term_cycles: number | null;
  term_start_rule: string | null;
  cycle_body_name: string | null;
  cycle_body_years: number | null;
}

interface LegislativeBodyOption {
  id: string;
  name: string;
  cycle_years: number;
}

/**
 * The day a term starts when it isn't the first day of its cycle
 * (ELECTION_OUTCOMES_PRD.md §5.2). Values match the backend's rule names.
 */
const TERM_START_RULES = [
  { value: '', label: 'First day of the cycle' },
  { value: 'second_tuesday_jan', label: 'Second Tuesday of January' },
  { value: 'second_wednesday_jan', label: 'Second Wednesday of January' },
  { value: 'jan3', label: 'January 3' },
  { value: 'jan20', label: 'January 20' },
];

/** "4 years (2 × Colorado General Assembly) · starts second Tuesday of January" */
const termRuleSummary = (office: Office): string => {
  if (!office.cycle_body_name || !office.term_cycles) return 'Not set';
  const years = office.cycle_body_years ? office.term_cycles * office.cycle_body_years : null;
  const span = `${years ? `${years} years ` : ''}(${office.term_cycles} × ${office.cycle_body_name})`;
  const rule = TERM_START_RULES.find((entry) => entry.value === office.term_start_rule && entry.value !== '');
  return rule ? `${span} · starts ${rule.label.charAt(0).toLowerCase()}${rule.label.slice(1)}` : span;
};

interface DistrictScope {
  scope_key: string;
  label: string;
}

const inputClasses =
  'w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary';
const labelClasses = 'mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary';

const COLUMN_COUNT = 8;

const EMPTY_OFFICE_FORM = {
  id: '',
  office_key: '',
  display_label: '',
  office_level: 'state_legislative',
  scope: 'statewide',
  district_scope: '',
  sort_order: '',
  default_vote_for: '1',
  cycle_body_id: '',
  term_cycles: '',
  term_start_rule: '',
};

const apiError = (error: any, fallback: string) =>
  error?.response?.data?.error ?? error?.response?.data?.message ?? fallback;

/**
 * Offices — the global office catalog shared by all election cycles.
 * scope/district type are immutable after creation (contests depend on them);
 * delete stays blocked by the backend while any contest references the office.
 */
const OfficesView: React.FC = () => {
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<'add' | 'edit' | null>(null);
  const [form, setForm] = useState(EMPTY_OFFICE_FORM);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null);

  const offices = useQuery({
    queryKey: ['admin-elections', 'offices'],
    queryFn: async () => {
      const response = await api.get('/contests/offices');
      return (response.data.data ?? []) as Office[];
    },
  });

  const districtScopes = useQuery({
    queryKey: ['admin-elections', 'district-scopes'],
    queryFn: async () => {
      const response = await api.get('/contests/district-scopes');
      return (response.data.data ?? []) as DistrictScope[];
    },
    staleTime: 5 * 60 * 1000,
  });

  // Term rules follow a legislative body's cycles (ELECTION_OUTCOMES_PRD.md §5.2).
  const bodies = useQuery({
    queryKey: ['admin-legislature', 'bodies', 'CO'],
    queryFn: async () => {
      const response = await api.get('/admin/legislature/bodies', { params: { state: 'CO' } });
      return (response.data.data ?? []) as LegislativeBodyOption[];
    },
    staleTime: 5 * 60 * 1000,
    enabled: modal !== null,
  });

  const termRulePayload = () => ({
    cycle_body_id: form.cycle_body_id || null,
    term_cycles: form.term_cycles.trim() === '' ? null : parseInt(form.term_cycles, 10),
    term_start_rule: form.term_start_rule || null,
  });

  const scopeLabel = (scopeKey: string | null) =>
    (districtScopes.data ?? []).find((entry) => entry.scope_key === scopeKey)?.label ?? scopeKey ?? '—';

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin-elections', 'offices'] });

  const saveMutation = useMutation({
    mutationFn: () => {
      if (modal === 'add') {
        return api.post('/contests/offices', {
          office_key: form.office_key.trim(),
          display_label: form.display_label.trim(),
          office_level: form.office_level,
          scope: form.scope,
          district_scope: form.scope === 'by_district' ? form.district_scope : null,
          sort_order: form.sort_order.trim() === '' ? null : parseInt(form.sort_order, 10),
          default_vote_for: parseInt(form.default_vote_for, 10) || 1,
          ...termRulePayload(),
        });
      }
      return api.put(`/contests/offices/${form.id}`, {
        display_label: form.display_label.trim(),
        office_level: form.office_level,
        sort_order: form.sort_order.trim() === '' ? null : parseInt(form.sort_order, 10),
        default_vote_for: parseInt(form.default_vote_for, 10) || 1,
        ...termRulePayload(),
      });
    },
    onSuccess: () => {
      toast.success(modal === 'add' ? 'Office added' : 'Office updated');
      setModal(null);
      setForm(EMPTY_OFFICE_FORM);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to save office')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/contests/offices/${id}`),
    onSuccess: () => {
      toast.success('Office deleted');
      setDeleteTarget(null);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to delete office')),
  });

  const saveDisabled =
    form.display_label.trim() === '' ||
    (modal === 'add' &&
      (form.office_key.trim() === '' || (form.scope === 'by_district' && form.district_scope === '')));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button variant="primary" size="sm" icon={Plus}
          onClick={() => { setForm(EMPTY_OFFICE_FORM); setModal('add'); }}>
          Add Office
        </Button>
      </div>

      {offices.isError ? (
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
            Couldn't load the office catalog.
          </p>
          <Button variant="outline" size="sm" onClick={() => offices.refetch()}>Retry</Button>
        </div>
      ) : (
        <StandardTable horizontalScroll loading={offices.isFetching && !offices.isLoading}>
          <StandardTableHeader>
            <StandardTableRow>
              <StandardTableHeaderCell columnType="narrow">Order</StandardTableHeaderCell>
              <StandardTableHeaderCell>Office</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="md">Level</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="lg">Scope</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="lg">District Type</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="xl">Vote For</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="md">Term</StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="actions">
                <span className="inline-flex items-center gap-1.5">
                  Actions
                  <ActionsHeaderHelp icons={['edit', 'delete']} />
                </span>
              </StandardTableHeaderCell>
            </StandardTableRow>
          </StandardTableHeader>
          <StandardTableBody>
            {offices.isLoading ? (
              <LoadingTableState colSpan={COLUMN_COUNT} />
            ) : (offices.data ?? []).length === 0 ? (
              <EmptyTableState
                colSpan={COLUMN_COUNT}
                message="No offices in the catalog"
                icon={<Landmark className="h-8 w-8" />}
              />
            ) : (
              (offices.data ?? []).map((office) => (
                <StandardTableRow key={office.id}>
                  <StandardTableCell>{office.sort_order ?? '—'}</StandardTableCell>
                  <StandardTableCell>
                    <div className="font-medium text-text-primary dark:text-text-dark-primary">
                      {office.display_label}
                    </div>
                    <div className="font-mono text-xs text-text-tertiary dark:text-text-dark-tertiary">
                      {office.office_key}
                    </div>
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="md">{officeLevelLabel(office.office_level)}</StandardTableCell>
                  <StandardTableCell responsiveHide="lg">
                    {office.scope === 'by_district' ? 'By district' : 'Statewide'}
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="lg">
                    {office.scope === 'by_district' ? scopeLabel(office.district_scope) : '—'}
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="xl">{office.default_vote_for ?? 1}</StandardTableCell>
                  <StandardTableCell responsiveHide="md">
                    <span className={office.cycle_body_name ? undefined : 'text-text-tertiary dark:text-text-dark-tertiary'}>
                      {termRuleSummary(office)}
                    </span>
                  </StandardTableCell>
                  <StandardTableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" icon={Pencil} ariaLabel={`Edit ${office.display_label}`} title={`Edit ${office.display_label}`}
                        onClick={() => {
                          setForm({
                            id: office.id,
                            office_key: office.office_key,
                            display_label: office.display_label,
                            office_level: office.office_level ?? 'state_legislative',
                            scope: office.scope ?? 'statewide',
                            district_scope: office.district_scope ?? '',
                            sort_order: office.sort_order != null ? String(office.sort_order) : '',
                            default_vote_for: String(office.default_vote_for ?? 1),
                            cycle_body_id: office.cycle_body_id ?? '',
                            term_cycles: office.term_cycles != null ? String(office.term_cycles) : '',
                            term_start_rule: office.term_start_rule ?? '',
                          });
                          setModal('edit');
                        }} />
                      <Button variant="ghost" size="sm" icon={Trash2} ariaLabel={`Delete ${office.display_label}`} title={`Delete ${office.display_label}`}
                        onClick={() => setDeleteTarget({ id: office.id, label: office.display_label })} />
                    </div>
                  </StandardTableCell>
                </StandardTableRow>
              ))
            )}
          </StandardTableBody>
        </StandardTable>
      )}

      <AdminModal
        isOpen={modal !== null}
        title={modal === 'add' ? 'Add Office' : 'Edit Office'}
        onClose={() => setModal(null)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setModal(null)}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={saveDisabled}
              loading={saveMutation.isLoading} onClick={() => saveMutation.mutate()}>
              Save Office
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {modal === 'add' && (
            <div>
              <label htmlFor="office-key" className={labelClasses}>Office Key *</label>
              <input id="office-key" type="text" value={form.office_key}
                placeholder="e.g. co_state_senate"
                onChange={(event) => setForm((current) => ({ ...current, office_key: event.target.value }))}
                className={`${inputClasses} font-mono`} />
            </div>
          )}
          <div>
            <label htmlFor="office-label" className={labelClasses}>Display Label *</label>
            <input id="office-label" type="text" value={form.display_label}
              onChange={(event) => setForm((current) => ({ ...current, display_label: event.target.value }))}
              className={inputClasses} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="office-level" className={labelClasses}>Level</label>
              <select id="office-level" value={form.office_level}
                onChange={(event) => setForm((current) => ({ ...current, office_level: event.target.value }))}
                className={inputClasses}>
                {OFFICE_LEVELS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="office-scope" className={labelClasses}>Scope</label>
              {modal === 'add' ? (
                <select id="office-scope" value={form.scope}
                  onChange={(event) => setForm((current) => ({ ...current, scope: event.target.value }))}
                  className={inputClasses}>
                  {OFFICE_SCOPES.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              ) : (
                <input id="office-scope" type="text" disabled
                  value={form.scope === 'by_district' ? 'By district (immutable)' : 'Statewide (immutable)'}
                  className={`${inputClasses} opacity-60`} />
              )}
            </div>
          </div>
          {modal === 'add' && form.scope === 'by_district' && (
            <div>
              <label htmlFor="office-district-scope" className={labelClasses}>District Type *</label>
              <select id="office-district-scope" value={form.district_scope}
                onChange={(event) => setForm((current) => ({ ...current, district_scope: event.target.value }))}
                className={inputClasses}>
                <option value="">Select a district type…</option>
                {(districtScopes.data ?? []).map((scope) => (
                  <option key={scope.scope_key} value={scope.scope_key}>{scope.label}</option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="office-sort" className={labelClasses}>Ballot Order</label>
              <input id="office-sort" type="number" value={form.sort_order}
                placeholder="auto"
                onChange={(event) => setForm((current) => ({ ...current, sort_order: event.target.value }))}
                className={inputClasses} />
            </div>
            <div>
              <label htmlFor="office-votefor" className={labelClasses}>Vote For (seats)</label>
              <input id="office-votefor" type="number" min={1} value={form.default_vote_for}
                onChange={(event) => setForm((current) => ({ ...current, default_vote_for: event.target.value }))}
                className={inputClasses} />
            </div>
          </div>

          <fieldset className="space-y-3 rounded-md border border-border-light p-3 dark:border-border-dark">
            <legend className="flex items-center gap-1.5 px-1 text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
              Term
              <HelpTrigger contentKey="legislative-cycles" size="sm" ariaLabel="How an office's term follows the cycles" />
            </legend>
            <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
              Officeholder terms start and end with a legislature's cycles: a term lasts this many cycles, from the
              cycle after the election.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="office-cycle-body" className={labelClasses}>Follows the cycles of</label>
                <select id="office-cycle-body" value={form.cycle_body_id} className="input"
                  onChange={(event) => setForm((current) => ({ ...current, cycle_body_id: event.target.value }))}>
                  <option value="">Not set</option>
                  {(bodies.data ?? []).map((body) => (
                    <option key={body.id} value={body.id}>{body.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="office-term-cycles" className={labelClasses}>Cycles per term</label>
                <input id="office-term-cycles" type="number" min={1} max={6} value={form.term_cycles} className="input"
                  onChange={(event) => setForm((current) => ({ ...current, term_cycles: event.target.value }))} />
              </div>
            </div>
            <div>
              <label htmlFor="office-term-start" className={labelClasses}>Term starts on</label>
              <select id="office-term-start" value={form.term_start_rule} className="input"
                onChange={(event) => setForm((current) => ({ ...current, term_start_rule: event.target.value }))}>
                {TERM_START_RULES.map((rule) => (
                  <option key={rule.value} value={rule.value}>{rule.label}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                Set only when the office's own start day differs from the legislature's — the Governor and
                statewide officers start on the second Tuesday of January, usually the day before the General Assembly convenes.
              </p>
            </div>
          </fieldset>
        </div>
      </AdminModal>

      <ConfirmationDialog
        isOpen={deleteTarget !== null}
        title="Delete office"
        message={
          deleteTarget
            ? `Delete "${deleteTarget.label}" from the office catalog? Deletion is blocked if any contest still uses this office.`
            : ''
        }
        confirmText="Delete"
        variant="danger"
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default OfficesView;
